import React from 'react';
import { Link } from 'react-router-dom';
import { dollarFormat, getPropByString, toTitleCase } from 'utils/utils';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import constants from 'constants/index';

export const handleTemporaryFundNavigationClick = (p) => {
  const { rowData, caseView, setActiveIndex, temporaryFundsTabIndex, onNavigateToCaseTemporaryFunds } = p || {};
  if (!caseView && rowData?.case_id && typeof onNavigateToCaseTemporaryFunds === 'function') {
    onNavigateToCaseTemporaryFunds(rowData);
    return;
  }
  const index = temporaryFundsTabIndex !== undefined ? temporaryFundsTabIndex : 8;
  if (typeof setActiveIndex === 'function') setActiveIndex(index);
};

/**
 * Case Name column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Case Name cell
 */
export const caseNameBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Case Name</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Case Name</span>
      <span className="text-break">
        <Link
          className="cursor-pointer text-decoration-none F-size14 call-back"
          to={`/${rowData?.is_lead ? 'leads' : 'cases'}/${rowData?.case_id}`}
          title={getPropByString(rowData, 'case_name')}
        >
          {getPropByString(rowData, 'case_name') || '-'}
        </Link>
      </span>
    </React.Fragment>
  );
};

/**
 * Date of Transaction column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {Object} params.userContext - User context
 * @returns {JSX.Element} - Date cell
 */
export const dateBody = (params) => {
  const { rowData, userContext } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Date of Transaction</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  const formattedDate = convertDateFromApiToUserTimezone(
    rowData?.record_created_date,
    userContext?.userDetails?.timezone,
    constants.month_date_year_format
  );

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Date of Transaction</span>
      <span className="text-break" title={formattedDate !== '-' ? formattedDate : undefined}>
        {formattedDate}
      </span>
    </React.Fragment>
  );
};

/**
 * Paid From column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {Function} params.setActiveIndex - Function to set active tab index
 * @param {number} params.temporaryFundsTabIndex - Temporary funds tab index
 * @returns {JSX.Element} - Paid From cell
 */
export const paidFromBody = (params) => {
  const { rowData, setActiveIndex, temporaryFundsTabIndex, caseView, onNavigateToCaseTemporaryFunds } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid From</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  const { payment_type, entry_type, paid_from } = rowData || {};

  if (payment_type === 'card_payment') {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid From</span>
        <span className="text-break" title="Temporary Funds">
          <Link
            to={`/contacts/${rowData?.contact_id}`}
            className="call-back text-decoration-none"
            title={paid_from ? paid_from : undefined}
          >
            {paid_from ? paid_from : '-'}
          </Link>
        </span>
      </React.Fragment>
    );
  }

  // Show "Cheque" if entry_type is cr and payment_type is designated_as_temporary_trust_funds_cheque
  if (entry_type === 'cr' && payment_type === 'designated_as_temporary_trust_funds_cheque') {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid From</span>
        <span className="text-break" title="Cheque">
          {paid_from}
        </span>
      </React.Fragment>
    );
  }

  if (entry_type === 'cr' && payment_type === 'designated_as_available_trust_funds') {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid From</span>
        <span
          className="text-break call-back"
          title={paid_from ? paid_from : undefined}
          onClick={() =>
            handleTemporaryFundNavigationClick({
              rowData,
              caseView,
              setActiveIndex,
              temporaryFundsTabIndex,
              onNavigateToCaseTemporaryFunds,
            })
          }
          style={{ color: '#2655B9', cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleTemporaryFundNavigationClick({
                rowData,
                caseView,
                setActiveIndex,
                temporaryFundsTabIndex,
                onNavigateToCaseTemporaryFunds,
              });
            }
          }}
        >
          {paid_from ? paid_from : '-'}
        </span>
      </React.Fragment>
    );
  }

  if (entry_type === 'db' && payment_type === 'client_withdrawal_temporary_funds') {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid From</span>
        <span
          className="text-break call-back"
          title={paid_from ? paid_from : undefined}
          onClick={() =>
            handleTemporaryFundNavigationClick({
              rowData,
              caseView,
              setActiveIndex,
              temporaryFundsTabIndex,
              onNavigateToCaseTemporaryFunds,
            })
          }
          style={{ color: '#2655B9', cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleTemporaryFundNavigationClick({
                rowData,
                caseView,
                setActiveIndex,
                temporaryFundsTabIndex,
                onNavigateToCaseTemporaryFunds,
              });
            }
          }}
        >
          {paid_from ? paid_from : '-'}
        </span>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Paid From</span>
      <span className="text-break">
        <Link to="/settings/accounts" className="call-back text-decoration-none" title={paid_from ? paid_from : undefined}>
          {paid_from ? paid_from : '-'}
        </Link>
      </span>
    </React.Fragment>
  );
};

/**
 * Payment Type column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Payment Type cell
 */
export const paymentTypeBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Payment Type</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  let displayPaymentType = rowData?.payment_type;

  if (displayPaymentType === 'designated_as_temporary_trust_funds_cheque') {
    displayPaymentType = 'designated_as_temporary_trust_funds (Cheque)';
  } else if (displayPaymentType === 'split_settlement') {
    displayPaymentType = 'Settlement';
  }

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Payment Type</span>
      <span title={toTitleCase(displayPaymentType)}>{toTitleCase(displayPaymentType)}</span>
    </React.Fragment>
  );
};

/**
 * Amount column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Amount cell
 */
export const amountBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Amount</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  const entry_type = rowData?.entry_type;
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Amount</span>
      <span>
        <span className={entry_type === 'cr' ? 'text-success' : 'text-danger'} title={dollarFormat(rowData?.amount)}>
          {dollarFormat(rowData?.amount)}
        </span>
      </span>
    </React.Fragment>
  );
};

/**
 * Paid To column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {Function} params.setActiveIndex - Function to set active tab index
 * @param {number} params.temporaryFundsTabIndex - Temporary funds tab index
 * @returns {JSX.Element} - Paid To cell
 */
export const paidToBody = (params) => {
  const { rowData, setActiveIndex, temporaryFundsTabIndex, caseView, onNavigateToCaseTemporaryFunds } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid To</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  const { payment_type, entry_type, credit_account_name, paid_to } = rowData || {};

  // Show "Temporary Funds" if payment_type is designated_as_temporary_trust_funds and entry_type is db
  if (payment_type === 'designated_as_temporary_trust_funds' && entry_type === 'db') {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid To</span>
        <span
          title={paid_to ? paid_to : undefined}
          onClick={() =>
            handleTemporaryFundNavigationClick({
              rowData,
              caseView,
              setActiveIndex,
              temporaryFundsTabIndex,
              onNavigateToCaseTemporaryFunds,
            })
          }
          style={{ color: '#2655B9', cursor: 'pointer' }}
          className="call-back"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleTemporaryFundNavigationClick({
                rowData,
                caseView,
                setActiveIndex,
                temporaryFundsTabIndex,
                onNavigateToCaseTemporaryFunds,
              });
            }
          }}
        >
          {paid_to ? paid_to : '-'}
        </span>
      </React.Fragment>
    );
  }

  if (entry_type === 'cr' && payment_type === 'designated_as_temporary_trust_funds_cheque') {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid To</span>
        <span
          title={paid_to ? paid_to : undefined}
          onClick={() =>
            handleTemporaryFundNavigationClick({
              rowData,
              caseView,
              setActiveIndex,
              temporaryFundsTabIndex,
              onNavigateToCaseTemporaryFunds,
            })
          }
          style={{ color: '#2655B9', cursor: 'pointer' }}
          className="call-back"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleTemporaryFundNavigationClick({
                rowData,
                caseView,
                setActiveIndex,
                temporaryFundsTabIndex,
                onNavigateToCaseTemporaryFunds,
              });
            }
          }}
        >
          {paid_to ? paid_to : '-'}
        </span>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Paid To</span>
      <span>
        {credit_account_name ? (
          <Link to="/settings/accounts" className="call-back text-decoration-none" title={paid_to ? paid_to : undefined}>
            {paid_to ? paid_to : '-'}
          </Link>
        ) : (
          <Link
            to={`/contacts/${
              rowData?.provider?.contact_id ||
              rowData?.vendor?.contact_id ||
              rowData?.checked_contact?.contact_id ||
              rowData?.contact_id ||
              rowData?.referral_contact?.contact_id
            }`}
            className="call-back text-decoration-none"
            title={paid_to ? paid_to : undefined}
          >
            {paid_to ? paid_to : '-'}
          </Link>
        )}
      </span>
    </React.Fragment>
  );
};

/**
 * Reference column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Reference cell
 */
export const referenceBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Ref.</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Ref.</span>
      <span className="text-break" title={rowData?.related_to || undefined}>
        {rowData?.related_to || '-'}
      </span>
    </React.Fragment>
  );
};

/**
 * Row className function for strikethrough styling
 * @param {Object} rowData - Row data
 * @returns {Object} - Object with className
 */
export const rowClassName = (rowData) => {
  return { strikethrough: rowData?.is_strikethrough };
};
